import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NotificationService } from '../../../../services/notification.service'; // NotificationService;
import { Subject, firstValueFrom } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { loadStripe } from '@stripe/stripe-js';
import { environment } from '../../../../../environments/environment';
import { Order } from '../../../../orders/models/order.models';
import {
  PaymentService,
  PaymentTransactionHistory,
  CreatePaymentLinkRequest,
  CreatePaymentLinkResponse,
  isPaymentTransactionAmountInclusiveOfAddons,
  parseTransactionHistoryResponse,
  resolvePaymentTransactionDiscount,
  resolvePaymentTransactionPaidAmount,
  sumPaymentTransactionAddonAmount,
  CardDetails,
  BillingDetails,
} from '../../../../services/payment.service';
import { PdfService, InvoiceData } from '../../../../services/pdf.service';
import { ReceiptPrinterService } from '../../../../services/receipt-printer.service';
import { ProfileService } from '../../../../services/profile.service';
import { OrderReceiptService } from '../../../../services/order-receipt.service';
import { ApiService } from '../../../../services/api.service';
import { LoggerService } from '../../../../services/logger.service';
import {
  PhoneValidator,
  USA_PHONE_CUSTOM_INPUT_RULE,
} from '../../../../shared/validators/phone.validator';
import {
  isBuiltInPaymentMethodCode,
  normalizePaymentMethodValue,
  PaymentMethodCode,
} from '../../../../shared/types/payment-method.enum';
import { extractErrorMessage, sanitizeErrorMessage } from '../../../../shared/utils/error.util';
import {
  MEMBERSHIP_ACTIVATION_PENDING_MESSAGE,
  isMembershipActivationPending,
  isMembershipEntitledResponse,
  isPaymentRequiredError,
  isRejectedPaymentResponse,
  resolveMembershipFailureMessage,
} from '../../../../shared/utils/membership-payment-status.util';
import { PaymentInfoCardComponent } from '../../../payment-info-card/payment-info-card.component';
import {
  PaymentTransactionCardComponent,
  PaymentLineItem,
} from '../../../payment-transaction-card/payment-transaction-card.component';
import { CustomInputComponent, ValidationRule } from '../../../custom-input/custom-input.component';
import { PaymentLinkSentModalComponent } from '../../../payment-link-sent-modal/payment-link-sent-modal.component';
import {
  createStripeElementConfig,
  STRIPE_ELEMENTS_APPEARANCE,
} from '../../../../shared/utils/stripe-elements.util';
import {
  BuiltInPaymentMethod,
  OrderDetailsStateService,
  PaymentMethod,
} from '../../services/order-details-state.service';
import { OrderDetailsTapToPayService } from '../../services/order-details-tap-to-pay.service';
import { TapToPayAndroidNativeService } from '../../../../services/tap-to-pay-android-native.service';
import { OrderUtils } from '../../../../orders/utils/order.utils';
import { OrdersService } from '../../../../orders/services/orders.service';
import { normalizeOrderRequestWashType } from '../../../notification-modal/utils/notification-order-request-wash-type.util';
import { getIsMobileOrTablet } from '../../../../shared/utils/device.util';
import { cardBrandIcon, cardBrandName } from '../../../../shared/utils/card-brand-icon.util';
import { PaymentInfo, resolvePaymentInfo } from '../../../../shared/utils/payment-info.util';
import {
  formatDateInTimeZone,
  formatDateTimeInTimeZone,
  getUserTimezone,
} from '../../../../shared/utils/date.util';

interface PaymentModeApiItem {
  id: string;
  name: string;
  is_active: boolean;
  wash_types: string[];
}

interface DynamicPaymentMethodOption {
  id: string;
  name: string;
  washTypes: ('single_wash' | 'monthly_membership' | 'add_on')[];
}

@Component({
  selector: 'app-payment-tab',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PaymentInfoCardComponent,
    PaymentTransactionCardComponent,
    CustomInputComponent,
    PaymentLinkSentModalComponent,
  ],
  templateUrl: './payment-tab.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
})
export class PaymentTabComponent implements OnChanges, OnDestroy {
  readonly PaymentMethodCode = PaymentMethodCode;
  @Input() order: Order | null = null;
  @Output() orderUpdated = new EventEmitter<void>();
  @Output() paymentCompleted = new EventEmitter<void>();

  readonly paymentPhoneValidation: ValidationRule[] = [
    { type: 'required', message: 'Phone Number is required' },
    USA_PHONE_CUSTOM_INPUT_RULE,
  ];

  // Stripe Elements
  stripe: any;
  elements: any;
  cardNumberElement: any;
  cardExpiryElement: any;
  cardCvcElement: any;

  private readonly destroy$ = new Subject<void>();
  /** Resolved once — avoids re-parsing shop timezone from localStorage on every CD pass. */
  private readonly shopTimezone = getUserTimezone();

  isLoadingDynamicPaymentMethods = false;
  dynamicPaymentMethodOptions: DynamicPaymentMethodOption[] = [];

  /**
   * Initializes the payment tab component, subscribes to successful Tap to Pay payment events, and refreshes order details and transaction history when a payment succeeds.
   * @example
   * onstructor(state, ttpService, paymentService, pdfService, profileService, apiService, messageService, logger)
   * undefined
   * @param {OrderDetailsStateService} state - Service used to manage order details state.
   * @param {OrderDetailsTapToPayService} ttpService - Service that exposes Tap to Pay payment success events.
   * @param {PaymentService} paymentService - Service used for payment-related operations.
   * @param {PdfService} pdfService - Service used for PDF generation and handling.
   * @param {ProfileService} profileService - Service used to access profile information.
   * @param {ApiService} apiService - Service used for API communication.
   * @param {NotificationService} messageService - Service used for displaying user messages.
   * @param {LoggerService} logger - Service used for logging application activity.
   * @returns {void} Does not return a value.
   **/
  constructor(
    readonly state: OrderDetailsStateService,
    readonly ttpService: OrderDetailsTapToPayService,
    private readonly tapToPayAndroidNative: TapToPayAndroidNativeService,
    private readonly paymentService: PaymentService,
    private readonly ordersService: OrdersService,
    private readonly pdfService: PdfService,
    private readonly receiptPrinterService: ReceiptPrinterService,
    private readonly profileService: ProfileService,
    private readonly orderReceiptService: OrderReceiptService,
    private readonly apiService: ApiService,
    private readonly messageService: NotificationService,
    private readonly logger: LoggerService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['order'] && this.order) {
      this.updateDefaultPaymentMethod();
      this.loadDynamicPaymentMethods();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.unmountStripeElements();
  }

  // ── Public methods ─────────────────────────────────────────────────────────

  /** Called by orchestrator via @ViewChild when payment needs a refresh. */
  refreshPaymentTab(): void {
    if (this.order?.id) {
      this.fetchTransactionHistory();
      this.apiService
        .get(`order/${this.order.id}`)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (orderResponse: any) => {
            const apiOrder = orderResponse?.data || orderResponse;
            if (apiOrder && this.order) {
              const newStatus = this.mapOrderStatus(apiOrder.order_status, apiOrder.payment_status);
              this.order.status = newStatus;
            }
          },
          error: () => {
            /* intentionally empty */
          },
        });
    }
  }

  /**
   * Initializes payment tab state, loads related data, and mounts Stripe elements when needed.
   * @example
   * nitializeOnTabOpen()
   * undefined
   * @returns {void} No return value.
   **/
  initializeOnTabOpen(): void {
    this.updateDefaultPaymentMethod();
    this.loadDynamicPaymentMethods();
    this.fetchTransactionHistory();
    if (this.order?.phone && !this.state.paymentForm.phoneNumber) {
      this.state.paymentForm.phoneNumber = this.order.phone;
    }
    const paymentTabDeferMs = 400;
    setTimeout(() => {
      if (this.state.paymentForm.method === PaymentMethodCode.Card) {
        this.initializeAndMountStripeElements();
      }
    }, paymentTabDeferMs);
  }

  // ── Device / native host ────────────────────────────────────────────────────

  get isMobileOrTablet(): boolean {
    return getIsMobileOrTablet();
  }

  /** Android Tap to Pay WebView exposes `window.TapToPayAndroid.startPayment`. */
  get isTapToPayNativeHost(): boolean {
    return this.tapToPayAndroidNative.isAvailable;
  }

  // ── Payment method selection ───────────────────────────────────────────────

  isCashPaymentDisabled(): boolean {
    return this.isMembershipOrder();
  }

  /** True when the open order is a monthly membership checkout. */
  isMembershipOrder(): boolean {
    if (!this.order) return false;
    const washType = this.order.washTypeValue || '';
    if (washType === 'monthly_membership' || washType === 'monthly') return true;
    const washTypeStr = this.order.washType || '';
    return (
      washTypeStr.toLowerCase().includes('monthly') ||
      washTypeStr.toLowerCase().includes('membership')
    );
  }

  /**
   * Selects a payment method, validates cash availability, initializes card payment if needed, and auto-fills the phone number for link payments when available.
   * @example
   * electPaymentMethod('card')
   * undefined
   * @param {PaymentMethod} method - The payment method to set for the order.
   * @returns {void} Returns nothing.
   **/
  selectPaymentMethod(method: PaymentMethod): void {
    if (method === PaymentMethodCode.TapToPay && !this.isMobileOrTablet) {
      return;
    }
    if (method === PaymentMethodCode.Cash && this.isCashPaymentDisabled()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Cash Payment Not Available',
        detail:
          'Cash payment is only available for single wash orders. Monthly subscriptions require card payment.',
        life: 3000,
      });
      return;
    }
    this.state.paymentForm.method = method;
    if (method === PaymentMethodCode.Cash) {
      // Open drawer when staff selects Cash so they can take tender (not on Print Receipt).
      void this.receiptPrinterService.openCashDrawer();
    }
    if (
      method === PaymentMethodCode.Link &&
      !this.state.paymentForm.phoneNumber &&
      this.order?.phone
    ) {
      this.state.paymentForm.phoneNumber = this.order.phone;
    }
    if (
      method === PaymentMethodCode.TapToPay ||
      method === PaymentMethodCode.Cash ||
      this.isDynamicPaymentMethod(method)
    ) {
      return;
    }
    if (method === PaymentMethodCode.Card) {
      this.initializeAndMountStripeElements();
    }
  }

  toggleMorePaymentMethods(): void {
    this.state.showMorePaymentMethods = !this.state.showMorePaymentMethods;
  }

  get filteredDynamicPaymentMethodOptions(): DynamicPaymentMethodOption[] {
    const currentWashType = this.getNormalizedWashType();
    const washTypeForFilter =
      currentWashType === 'prepaid_wash_book' ? 'single_wash' : currentWashType;
    return this.dynamicPaymentMethodOptions.filter(option =>
      option.washTypes.includes(washTypeForFilter)
    );
  }

  isDynamicPaymentMethod(method: string | null | undefined): boolean {
    return !!method && !this.isBuiltInPaymentMethod(method);
  }

  isDynamicPaymentMethodSelected(methodName: string): boolean {
    return this.state.paymentForm.method === methodName;
  }

  isPaymentPending(): boolean {
    if (!this.order) return false;
    const isPendingStatus = this.order.status.value.toLowerCase() === 'pending';
    const hasNoTransactions = this.state.transactionHistories.length === 0;
    return isPendingStatus && hasNoTransactions && !this.state.isLoadingPaymentDetails;
  }

  /**
   * Determines whether the current payment form inputs are valid for the selected payment method.
   * @example
   * sPaymentFormValid()
   * true
   * @returns {boolean} True when the payment form is valid for the active payment method; otherwise, false.
   **/
  isPaymentFormValid(): boolean {
    if (
      this.state.paymentForm.method === PaymentMethodCode.TapToPay ||
      this.state.paymentForm.method === PaymentMethodCode.Cash ||
      this.isDynamicPaymentMethod(this.state.paymentForm.method)
    ) {
      return true;
    }
    if (this.state.paymentForm.method === PaymentMethodCode.Card) {
      const hasCardholder = Boolean(this.state.paymentForm.cardholderName?.trim());
      const usingStripeElements =
        this.cardNumberElement && this.cardExpiryElement && this.cardCvcElement;
      if (usingStripeElements) {
        return (
          hasCardholder &&
          this.state.stripeCardNumberComplete &&
          this.state.stripeCardExpiryComplete &&
          this.state.stripeCardCvcComplete
        );
      }
      return hasCardholder;
    }
    const phone = (this.state.paymentForm.phoneNumber || '').trim();
    return phone.length > 0 && PhoneValidator.isValidUsaPhone(phone);
  }

  // ── Tap to Pay ─────────────────────────────────────────────────────────────

  openTapToPayApp(): void {
    if (!this.order) return;
    this.ttpService.openTapToPayApp(
      this.order,
      this.state.paymentForm.phoneNumber,
      this.state.apiOrderData
    );
  }

  // ── Card payment ───────────────────────────────────────────────────────────

  /**
   * Processes a card payment for the current order using Stripe, including validation, payment method creation, and payment confirmation.
   * @example
   * processCardPayment()
   * Promise<void>
   * @returns {Promise<void>} Resolves when the payment flow has been initiated and any resulting UI/state updates have been handled.
   **/
  async processCardPayment(): Promise<void> {
    if (!this.isPaymentFormValid()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Payment Error',
        detail: 'Please fill in all card details correctly.',
      });
      return;
    }
    if (!this.order?.id) {
      this.messageService.add({
        severity: 'error',
        summary: 'Payment Error',
        detail: 'Order ID is missing. Please try again.',
      });
      return;
    }

    if (!this.stripe) await this.initializeStripeIfNeeded();
    this.state.isProcessingPayment = true;

    try {
      if (!this.cardNumberElement) {
        if (!this.stripe) await this.initializeStripeIfNeeded();
        this.mountStripeElements();
        await new Promise<void>(resolve => setTimeout(resolve, 300));
      }

      const cardNumberContainer = document.getElementById('card-number');
      if (cardNumberContainer && !cardNumberContainer.querySelector('.StripeElement')) {
        this.mountStripeElements();
        await new Promise<void>(resolve => setTimeout(resolve, 300));
      }

      if (!this.stripe || !this.cardNumberElement) {
        this.state.isProcessingPayment = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Payment Error',
          detail: 'Payment system not initialized. Please try again.',
        });
        return;
      }

      const { paymentMethod, error: pmError } = await this.stripe.createPaymentMethod({
        type: 'card',
        card: this.cardNumberElement,
        billing_details: {
          name: this.state.paymentForm.cardholderName || '',
          address: { line1: this.state.paymentForm.billingAddress || '' },
        },
      });

      if (pmError) {
        this.state.isProcessingPayment = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Payment Error',
          detail:
            pmError.message || 'Could not create payment method. Please check your card details.',
          life: 5000,
        });
        return;
      }

      const paymentMethodId = paymentMethod!.id;

      if (!this.state.paymentIntentClientSecret && this.isPaymentPending() && this.order.id) {
        try {
          const response = await firstValueFrom(
            this.paymentService.createPaymentIntent(this.order.id, paymentMethodId)
          );
          if (response?.data?.client_secret) {
            this.state.paymentIntentClientSecret = response.data.client_secret;
          }
        } catch (err: any) {
          this.state.isProcessingPayment = false;
          this.messageService.add({
            severity: 'error',
            summary: isPaymentRequiredError(err) ? 'Membership not activated' : 'Payment Error',
            detail: isPaymentRequiredError(err)
              ? resolveMembershipFailureMessage(err)
              : err?.error?.message ||
                err?.message ||
                'Could not prepare card payment. Please try again.',
            life: 5000,
          });
          return;
        }
      }

      const request = {
        order_id: this.order.id,
        payment_method_id: paymentMethodId ?? undefined,
      };

      this.paymentService
        .confirmCardPayment(request)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response: any) => {
            this.state.isProcessingPayment = false;
            // A 200 does not mean the payment was accepted: the backend reports a refused card
            // (including a membership card that cannot be saved for renewals) in the body.
            if (isRejectedPaymentResponse(response)) {
              this.state.paymentIntentClientSecret = '';
              this.messageService.add({
                severity: 'error',
                summary: 'Payment Error',
                detail: resolveMembershipFailureMessage(
                  response,
                  'Payment could not be completed. Please try again.'
                ),
                life: 6000,
              });
              this.fetchTransactionHistory();
              this.orderUpdated.emit();
              return;
            }

            if (this.isMembershipOrder()) {
              if (isMembershipActivationPending(response)) {
                this.messageService.add({
                  severity: 'info',
                  summary: 'Payment received',
                  detail: MEMBERSHIP_ACTIVATION_PENDING_MESSAGE,
                  life: 4000,
                });
                return;
              }

              if (!isMembershipEntitledResponse(response)) {
                this.state.paymentIntentClientSecret = '';
                this.messageService.add({
                  severity: 'error',
                  summary: 'Membership not activated',
                  detail: resolveMembershipFailureMessage(response),
                  life: 6000,
                });
                this.fetchTransactionHistory();
                this.orderUpdated.emit();
                return;
              }
            }
            this.paymentCompleted.emit();
          },
          error: () => {
            this.state.isProcessingPayment = false;
            setTimeout(() => {
              this.fetchTransactionHistory();
              this.orderUpdated.emit();
            }, 1000);
          },
        });
    } catch (err: any) {
      this.state.isProcessingPayment = false;
      this.messageService.add({
        severity: 'error',
        summary: 'Payment Error',
        detail:
          err?.error?.message || err?.message || 'Payment processing failed. Please try again.',
      });
    }
  }

  // ── Payment link ───────────────────────────────────────────────────────────

  /**
   * Creates a payment link for the current order using the provided phone number.
   * @example
   * createPaymentLink()
   * void
   * @param {void} None - This function does not accept any arguments.
   * @returns {void} No return value.
   **/
  createPaymentLink(): void {
    const phone = (this.state.paymentForm.phoneNumber || '').trim();
    if (!this.order?.id || !phone) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Order ID or phone number is missing',
      });
      return;
    }
    if (!PhoneValidator.isValidUsaPhone(phone)) {
      this.state.paymentLinkError = USA_PHONE_CUSTOM_INPUT_RULE.message;
      this.messageService.add({
        severity: 'error',
        summary: 'Payment Link Error',
        detail: this.state.paymentLinkError,
      });
      return;
    }

    this.state.isCreatingPaymentLink = true;
    this.state.paymentLinkError = '';

    const request: CreatePaymentLinkRequest = {
      order_id: this.order.id,
      phone_number: phone,
      email: this.order.email || undefined,
      source: this.order.source || 'web',
    };

    this.paymentService.createPaymentLink(request).subscribe({
      next: (response: CreatePaymentLinkResponse) => {
        this.state.isCreatingPaymentLink = false;
        if (response.status && response.paymentLink?.success) {
          this.messageService.add({
            severity: 'success',
            summary: 'Payment Link Created',
            detail: `Payment link sent to ${this.state.paymentForm.phoneNumber}`,
          });
          this.state.showPaymentLinkSentModal = true;
          setTimeout(() => {
            this.fetchTransactionHistory();
            this.orderUpdated.emit();
          }, 1000);
        } else {
          this.state.paymentLinkError = sanitizeErrorMessage(
            extractErrorMessage(response, 'Failed to create payment link. Please try again.')
          );
          this.messageService.add({
            severity: 'error',
            summary: 'Payment Link Error',
            detail: this.state.paymentLinkError,
          });
        }
      },
      error: error => {
        this.state.isCreatingPaymentLink = false;
        const errorMessage = sanitizeErrorMessage(
          extractErrorMessage(error, 'Failed to create payment link. Please try again.')
        );
        this.state.paymentLinkError = errorMessage;
        this.messageService.add({
          severity: 'error',
          summary: 'Payment Link Error',
          detail: errorMessage,
        });
      },
    });
  }

  // ── Cash / membership confirmation ─────────────────────────────────────────

  confirmCashPayment(): void {
    this.confirmPaymentMode(PaymentMethodCode.Cash as PaymentMethod);
  }

  /**
   * Confirms the selected payment mode for the current order and refreshes order data on success.
   * @example
   * onfirmPaymentMode('cash')
   * void
   * @param {'cash' | 'DRB Membership' | 'Prepaid' | 'Everwash Membership' | 'Way.com Membership'} paymentMode - The payment mode to confirm for the order.
   * @returns {void} No return value.
   **/
  confirmPaymentMode(paymentMode: PaymentMethod): void {
    if (!this.order?.id) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Required Field',
        detail: 'Order ID is required for payment confirmation',
      });
      return;
    }
    if (this.state.isConfirmingCashPayment) return;

    this.state.isConfirmingCashPayment = true;
    this.state.paymentStartTime = Date.now();

    this.logger.trackPayment('Payment confirmation started (order details)', {
      paymentMethod: paymentMode,
      orderId: this.order?.orderId,
      orderStatus: this.order?.status?.value,
      elapsedSinceOpenMs: this.state.modalOpenTime
        ? Date.now() - this.state.modalOpenTime
        : undefined,
    });

    this.apiService
      .post(`order/confirm-payment-mode/${this.order.id}`, { payment_mode: paymentMode })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.state.isConfirmingCashPayment = false;
          this.logger.trackPayment('Payment confirmed (order details)', {
            paymentMethod: paymentMode,
            orderId: this.order?.orderId,
            elapsedMs: Date.now() - this.state.paymentStartTime,
          });
          this.paymentCompleted.emit();
        },
        error: error => {
          this.state.isConfirmingCashPayment = false;
          this.logger.trackError('OrderDetailsModal.confirmPaymentMode', error, {
            paymentMethod: paymentMode,
            orderId: this.order?.orderId,
            elapsedMs: Date.now() - this.state.paymentStartTime,
          });
          this.messageService.add({
            severity: 'error',
            summary: 'Payment Error',
            detail:
              error?.error?.message ||
              error?.message ||
              `Failed to confirm ${paymentMode} payment. Please try again.`,
          });
        },
      });
  }

  // ── Invoice download ───────────────────────────────────────────────────────

  /**
   * Downloads and generates an invoice for the current order using business profile details, with a fallback to default invoice data if profile retrieval fails.
   * @example
   * nDownloadInvoice(transaction)
   * void
   * @param {PaymentTransactionHistory} transaction - The payment transaction history used to populate the invoice.
   * @returns {void} Does not return a value.
   **/
  onDownloadInvoice(transaction: PaymentTransactionHistory): void {
    if (!this.order) return;
    this.downloadInvoiceForOrder(
      transaction,
      this.order,
      this.state.cardDetails,
      this.state.billingDetails
    );
  }

  onPrintInvoice(transaction: PaymentTransactionHistory): void {
    if (!this.order) return;
    this.orderReceiptService.printReceipt({
      order: this.order,
      transaction,
      cardDetails: this.state.cardDetails,
      billingDetails: this.state.billingDetails,
    });
  }

  onDownloadOriginalOrderInvoice(transaction: PaymentTransactionHistory): void {
    const orderId = transaction.order_id || this.state.originalOrderId;
    if (!orderId) return;

    this.ordersService.getOrderById(orderId).subscribe({
      next: originalOrder => {
        this.downloadInvoiceForOrder(
          transaction,
          originalOrder,
          this.state.originalOrderCardDetails,
          this.state.originalOrderBillingDetails
        );
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Download failed',
          detail: 'Could not load the original order for invoice download.',
        });
      },
    });
  }

  hasOriginalOrderTransactionHistory(): boolean {
    return this.state.originalOrderTransactionHistories.length > 0;
  }

  // ── Transaction helpers ────────────────────────────────────────────────────

  formatAmountCents(amountInMinorUnits: string | number | null | undefined): string {
    const numeric = Number(amountInMinorUnits ?? 0);
    const dollars = isFinite(numeric) ? numeric : 0;
    return `$${dollars.toFixed(2)}`;
  }

  formatAmount(amount: string): string {
    if (!amount) return '$0.00';
    const numeric = Number(amount);
    if (!isFinite(numeric)) return '$0.00';
    return `$${numeric.toFixed(2)}`;
  }

  getTransactionItems(th: PaymentTransactionHistory): PaymentLineItem[] {
    const items: PaymentLineItem[] = [];

    // 1. Package base price.
    //    Determine if `amount` is the package-only price or includes add-ons.
    const isInclusive = isPaymentTransactionAmountInclusiveOfAddons(th);
    const addonSum = sumPaymentTransactionAddonAmount(th);
    /*
     * CWS-1444: `amount` is `orders.list_price`, frozen at signup, and is the
     * subtotal the discount line is derived from (subtotal - paid). That holds
     * for a signup and for a discounted renewal, which collect at or below the
     * list price.
     *
     * It breaks for a payment that collected MORE than the order's list price —
     * a plan-change upgrade charge, say: a $475 charge on a membership sold at
     * $25 rendered a "$25.00" line against a $475 total, and once the refund
     * line was added the card visibly failed to add up. A payment above the
     * frozen list price cannot be measured against it without implying a
     * negative discount, so the payment itself is the subtotal and there is no
     * discount to show — which matches the product rule that a plan change
     * carries none.
     */
    const orderAmount = Number(th.amount || 0);
    const paidAmount = resolvePaymentTransactionPaidAmount(th);
    const amountNum = paidAmount !== null && paidAmount > orderAmount ? paidAmount : orderAmount;
    const packageBasePrice = isInclusive ? Math.max(0, amountNum - addonSum) : amountNum;

    const serviceLabel =
      (typeof th.prepaid_wash_book_name === 'string' && th.prepaid_wash_book_name.trim()) ||
      (typeof th.package_name === 'string' && th.package_name.trim()) ||
      (th.wash_type === 'prepaid_wash_book' ? 'Prepaid Wash Book' : 'Car Wash');
    items.push({
      label: serviceLabel,
      amount: `$${packageBasePrice.toFixed(2)}`,
    });

    // 2. Add-on line items (one row each).
    const addons = Array.isArray(th.addons) ? th.addons : [];
    for (const addon of addons) {
      const count = Math.max(1, Number(addon.add_on_count || 1));
      const price = Number(addon.price ?? 0);
      const lineTotal = isFinite(price) && isFinite(count) ? price * count : 0;
      const countLabel = count > 1 ? ` ×${count}` : '';
      items.push({
        label: `${addon.addon_name || 'Add-on'}${countLabel}`,
        amount: `$${lineTotal.toFixed(2)}`,
      });
    }

    // 3. Discount — prefer discount_name, then voucher_code, then generic label.
    //    Derived from what this payment collected so the lines reconcile with the total shown
    //    below them; a later membership renewal can carry a different discount than signup.
    const subtotalBeforeDiscount = isInclusive ? amountNum : amountNum + addonSum;
    const discountAmt = resolvePaymentTransactionDiscount(th, subtotalBeforeDiscount);
    if (discountAmt > 0) {
      const discountLabel = th.discount_name
        ? `Discount (${th.discount_name})`
        : th.voucher_code
          ? `Discount (${th.voucher_code})`
          : 'Discount';
      items.push({ label: discountLabel, amount: `-$${discountAmt.toFixed(2)}` });
    }

    // 4. Taxes & Fees.
    items.push({ label: 'Taxes + Fees', amount: '$0.00' });

    // 5. Refund against this payment (CWS-1444). A membership downgrade refunds
    //    part of what was collected, and without this line the card showed the
    //    full charge with no sign that money had gone back. The figure is
    //    `payments.refunded_amount`, which is the cumulative total Stripe has
    //    refunded against the charge.
    const refunded = this.refundedAmountFor(th);
    if (refunded > 0) {
      items.push({ label: 'Refunded', amount: `-$${refunded.toFixed(2)}` });
    }

    return items;
  }

  /**
   * Amount refunded against this payment, in dollars, or 0.
   *
   * @param {PaymentTransactionHistory} th
   * @returns {number}
   */
  private refundedAmountFor(th: PaymentTransactionHistory): number {
    const raw = th.payment_refunded_amount;
    if (raw === null || raw === undefined || String(raw).trim() === '') {
      return 0;
    }
    const value = Number(raw);
    return Number.isFinite(value) && value > 0 ? value : 0;
  }

  /**
   * "Total Amount Paid" stops being true once part of it has been refunded, so a
   * refunded transaction says what the customer is actually out of pocket and
   * labels it as such. Without the relabel the lines would not add up to the
   * total and the card would look wrong.
   *
   * @param {PaymentTransactionHistory} th
   * @returns {string}
   */
  getTotalLabel(th: PaymentTransactionHistory): string {
    return this.refundedAmountFor(th) > 0 ? 'Net Amount Paid' : 'Total Amount Paid';
  }

  getTotalAmountPaid(th: PaymentTransactionHistory): string {
    // Net of any refund, so the breakdown above it adds up (CWS-1444). The label
    // changes with it; see getTotalLabel.
    const refunded = this.refundedAmountFor(th);
    if (refunded > 0) {
      const collected = this.collectedAmount(th);
      return `$${Math.max(0, collected - refunded).toFixed(2)}`;
    }
    return `$${Math.max(0, this.collectedAmount(th)).toFixed(2)}`;
  }

  /**
   * What this payment collected, before any refund.
   *
   * @param {PaymentTransactionHistory} th
   * @returns {number}
   */
  private collectedAmount(th: PaymentTransactionHistory): number {
    // What this payment actually collected, when the backend supplies it. Checked before
    // final_price because final_price belongs to the ORDER and is frozen at signup — on a
    // membership that renews at different amounts it reports the first invoice's total for
    // every renewal.
    const paid = resolvePaymentTransactionPaidAmount(th);
    if (paid !== null) {
      return paid;
    }
    // Prefer final_price — it's the server-confirmed charged amount.
    const fp =
      th.final_price != null && String(th.final_price).trim() !== '' ? Number(th.final_price) : NaN;
    if (isFinite(fp)) {
      return fp;
    }
    // Fallback: sum everything ourselves.
    const amountNum = Number(th.amount || 0);
    const addonSum = isPaymentTransactionAmountInclusiveOfAddons(th)
      ? 0
      : sumPaymentTransactionAddonAmount(th);
    const discount = Number(th.discount || 0);
    return amountNum + addonSum - discount;
  }

  formatPaymentDate(dateString: string): string {
    if (!dateString) return '';
    return formatDateInTimeZone(dateString, this.shopTimezone, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  formatPaymentDateTime(dateString: string): string {
    if (!dateString) return '';
    return formatDateTimeInTimeZone(dateString, this.shopTimezone);
  }

  /** The Payment Info box: the order's latest charge and the card it was made with. */
  private get paymentInfo(): PaymentInfo {
    return resolvePaymentInfo(
      this.state.transactionHistories,
      this.state.cardDetails,
      this.state.billingDetails
    );
  }

  getCardMask(): string {
    const card = this.paymentInfo.card;
    return card?.last4 ? `XXX-${card.last4}` : '';
  }

  hasCardDetails(): boolean {
    const last4 = this.paymentInfo.card?.last4;
    return typeof last4 === 'string' && last4.length > 0;
  }

  getCardExpirationDate(): string {
    if (!this.state.cardDetails?.exp_month || !this.state.cardDetails?.exp_year) return '';
    return `${String(this.state.cardDetails.exp_month).padStart(2, '0')}/${this.state.cardDetails.exp_year}`;
  }

  getCardBrandIcon(): string {
    return cardBrandIcon(this.paymentInfo.card?.brand);
  }

  getCardBrandName(): string {
    return cardBrandName(this.paymentInfo.card?.brand);
  }

  getTransactionId(): string {
    const paymentIntentId = this.paymentInfo.transaction?.payment_intent_id;
    return paymentIntentId ? `#${paymentIntentId.slice(-10)}` : '';
  }

  getBillingAddress(): string {
    const billing = this.paymentInfo.billing;
    if (!billing?.address) return '';
    const parts = [];
    if (billing.address.line1) parts.push(billing.address.line1);
    if (billing.address.country) parts.push(billing.address.country);
    return parts.join(', ');
  }

  getContactInfo(): string {
    const billing = this.paymentInfo.billing;
    const parts = [];
    if (billing?.email) parts.push(billing.email);
    if (billing?.phone) parts.push(billing.phone);
    if (parts.length === 0) {
      if (this.order?.email) parts.push(this.order.email);
      if (this.order?.phone) parts.push(this.order.phone);
    }
    return parts.join(', ');
  }

  getPaymentDate(): string {
    const paymentDate = this.paymentInfo.transaction?.payment_date;
    return paymentDate ? this.formatPaymentDateTime(paymentDate) : '';
  }

  // ── Payment link sent modal handlers ──────────────────────────────────────

  onPaymentLinkSentModalClose(): void {
    this.state.showPaymentLinkSentModal = false;
  }

  onCancelPayment(): void {
    this.state.showPaymentLinkSentModal = false;
  }

  onResendPaymentLink(): void {
    this.createPaymentLink();
  }

  // ── Stripe Elements ────────────────────────────────────────────────────────

  /**
   * Mounts the Stripe card number, expiry, and CVC elements into their respective containers and attaches validation listeners.
   * @example
   * mountStripeElements()
   * undefined
   * @returns {void} Does not return a value.
   **/
  mountStripeElements(): void {
    if (!this.cardNumberElement || !this.cardExpiryElement || !this.cardCvcElement) return;
    try {
      const cardNumberContainer = document.getElementById('card-number');
      const cardExpiryContainer = document.getElementById('card-expiry');
      const cardCvcContainer = document.getElementById('card-cvc');
      if (!cardNumberContainer || !cardExpiryContainer || !cardCvcContainer) return;

      if (cardNumberContainer.querySelector('.StripeElement')) {
        try {
          this.cardNumberElement.unmount();
        } catch {
          /* ignore */
        }
      }
      if (cardExpiryContainer.querySelector('.StripeElement')) {
        try {
          this.cardExpiryElement.unmount();
        } catch {
          /* ignore */
        }
      }
      if (cardCvcContainer.querySelector('.StripeElement')) {
        try {
          this.cardCvcElement.unmount();
        } catch {
          /* ignore */
        }
      }

      this.cardNumberElement.mount('#card-number');
      this.setupStripeElementListeners(this.cardNumberElement, 'card-number-error');
      this.cardExpiryElement.mount('#card-expiry');
      this.setupStripeElementListeners(this.cardExpiryElement, 'card-expiry-error');
      this.cardCvcElement.mount('#card-cvc');
      this.setupStripeElementListeners(this.cardCvcElement, 'card-cvc-error');
    } catch (error) {
      console.error('Error mounting Stripe elements:', error);
    }
  }

  // ── Data fetch helpers ─────────────────────────────────────────────────────

  /**
   * Fetches and stores the transaction history and related payment details for the current order.
   * @example
   * etchTransactionHistory()
   * void
   * @returns {void} Does not return a value.
   **/
  fetchTransactionHistory(): void {
    if (!this.order?.id) return;
    this.state.isLoadingPaymentDetails = true;
    this.state.paymentDetailsError = false;
    this.state.transactionHistories = [];
    this.state.originalOrderTransactionHistories = [];
    this.state.originalOrderId = null;
    this.state.originalOrderNumber = null;
    this.state.originalOrderCardDetails = null;
    this.state.originalOrderBillingDetails = null;
    this.state.cardDetails = null;
    this.state.billingDetails = null;

    this.paymentService.getTransactionHistory(this.order.id).subscribe({
      next: (res: any) => {
        this.state.isLoadingPaymentDetails = false;
        const th = res?.transactionHistory;
        if (!th) {
          this.state.paymentDetailsError = true;
          return;
        }
        const parsed = parseTransactionHistoryResponse(th);
        this.state.transactionHistories = parsed.transactions;
        this.state.cardDetails = parsed.cardDetails;
        this.state.billingDetails = parsed.billingDetails;
        this.state.originalOrderTransactionHistories = parsed.originalOrderTransactions;
        this.state.originalOrderId = parsed.originalOrderId;
        this.state.originalOrderNumber = parsed.originalOrderNumber;
        this.state.originalOrderCardDetails = parsed.originalOrderCardDetails;
        this.state.originalOrderBillingDetails = parsed.originalOrderBillingDetails;
      },
      error: () => {
        this.state.isLoadingPaymentDetails = false;
        this.state.paymentDetailsError = true;
        this.state.transactionHistories = [];
        this.state.originalOrderTransactionHistories = [];
        this.state.originalOrderId = null;
        this.state.originalOrderNumber = null;
        this.state.originalOrderCardDetails = null;
        this.state.originalOrderBillingDetails = null;
        this.state.cardDetails = null;
        this.state.billingDetails = null;
      },
    });
  }

  // ── Private helpers ────────────────────────────────────────────────────────

  /**
   * Updates the default payment method based on order type and device capabilities.
   * @example
   * updateDefaultPaymentMethod()
   * undefined
   * @returns {void} Does not return a value.
   **/
  private updateDefaultPaymentMethod(): void {
    if (!this.order) return;
    const isMobile = this.isMobileOrTablet;
    const isSubscription = this.isCashPaymentDisabled();
    if (isSubscription && !isMobile) {
      this.state.paymentForm.method = PaymentMethodCode.Card;
    } else if (this.state.paymentForm.method === PaymentMethodCode.Cash && isSubscription) {
      this.state.paymentForm.method = isMobile
        ? PaymentMethodCode.TapToPay
        : PaymentMethodCode.Card;
    } else if (
      !this.state.paymentForm.method ||
      this.state.paymentForm.method === PaymentMethodCode.Cash
    ) {
      this.state.paymentForm.method = isMobile
        ? PaymentMethodCode.TapToPay
        : PaymentMethodCode.Cash;
    }
    if (!isMobile && this.state.paymentForm.method === PaymentMethodCode.TapToPay) {
      this.state.paymentForm.method = isSubscription
        ? PaymentMethodCode.Card
        : PaymentMethodCode.Cash;
    }
    this.reconcilePaymentMethodAfterContextChange();
    if (this.state.paymentForm.method === PaymentMethodCode.Card) {
      setTimeout(() => this.initializeAndMountStripeElements(), 400);
    }
  }

  private loadDynamicPaymentMethods(): void {
    if (!this.order) {
      this.dynamicPaymentMethodOptions = [];
      return;
    }

    this.isLoadingDynamicPaymentMethods = true;
    this.apiService
      .get<PaymentModeApiItem[]>('user/payment-modes')
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoadingDynamicPaymentMethods = false;
        })
      )
      .subscribe({
        next: response => {
          const seen = new Set<string>();
          this.dynamicPaymentMethodOptions = (Array.isArray(response?.data) ? response.data : [])
            .filter(mode => !!mode?.is_active)
            .map(mode => this.mapDynamicPaymentMethod(mode))
            .filter((mode): mode is DynamicPaymentMethodOption => !!mode)
            .filter(mode => {
              const key = `${this.normalizePaymentMethodName(mode.name)}:${mode.washTypes.join('|')}`;
              if (seen.has(key)) {
                return false;
              }
              seen.add(key);
              return true;
            });

          this.reconcilePaymentMethodAfterContextChange();
        },
        error: error => {
          this.dynamicPaymentMethodOptions = [];
          this.reconcilePaymentMethodAfterContextChange();
          this.logger.trackError('OrderDetailsModal.loadDynamicPaymentMethods', error, {
            orderId: this.order?.orderId,
          });
        },
      });
  }

  private reconcilePaymentMethodAfterContextChange(): void {
    if (this.state.paymentForm.method === PaymentMethodCode.Cash && this.isCashPaymentDisabled()) {
      this.state.paymentForm.method = this.getDefaultPaymentMethod();
      return;
    }

    if (
      this.isDynamicPaymentMethod(this.state.paymentForm.method) &&
      !this.filteredDynamicPaymentMethodOptions.some(
        option => option.name === this.state.paymentForm.method
      )
    ) {
      this.state.paymentForm.method = this.getDefaultPaymentMethod();
    }
  }

  private getDefaultPaymentMethod(): PaymentMethod {
    const isMobile = this.isMobileOrTablet;
    const isSubscription = this.isCashPaymentDisabled();
    if (isSubscription) {
      return isMobile ? PaymentMethodCode.TapToPay : PaymentMethodCode.Card;
    }
    return isMobile ? PaymentMethodCode.TapToPay : PaymentMethodCode.Cash;
  }

  private isBuiltInPaymentMethod(
    method: string | null | undefined
  ): method is BuiltInPaymentMethod {
    return isBuiltInPaymentMethodCode(method);
  }

  private normalizePaymentMethodName(name: string | null | undefined): string {
    return String(name ?? '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '');
  }

  private normalizeWashType(washType: string): 'single_wash' | 'monthly_membership' | 'add_on' {
    return normalizeOrderRequestWashType(washType);
  }

  private getNormalizedWashType():
    'single_wash' | 'monthly_membership' | 'add_on' | 'prepaid_wash_book' {
    const raw =
      this.order?.washTypeValue || this.order?.washType || this.state.apiOrderData?.wash_type || '';
    const lower = String(raw).toLowerCase();
    if (lower.includes('prepaid')) {
      return 'prepaid_wash_book';
    }
    return this.normalizeWashType(String(raw));
  }

  private mapDynamicPaymentMethod(mode: PaymentModeApiItem): DynamicPaymentMethodOption | null {
    const name = String(mode?.name ?? '').trim();
    if (!name) {
      return null;
    }

    const normalizedName = this.normalizePaymentMethodName(name);
    if (
      isBuiltInPaymentMethodCode(name) ||
      normalizePaymentMethodValue(name) === PaymentMethodCode.CardPresent ||
      normalizedName === 'cardpayment' ||
      normalizedName === 'paymentlink'
    ) {
      return null;
    }

    const washTypes = Array.isArray(mode?.wash_types)
      ? Array.from(
          new Set(
            mode.wash_types
              .map(washType => this.normalizeWashType(String(washType)))
              .filter(
                (washType): washType is 'single_wash' | 'monthly_membership' | 'add_on' =>
                  !!washType
              )
          )
        )
      : [];

    if (!washTypes.length) {
      return null;
    }

    return {
      id: String(mode.id || name),
      name,
      washTypes,
    };
  }

  /**
   * Initializes Stripe Elements when the payment method is card and mounts them once the target containers are available.
   * @example
   * initializeAndMountStripeElements(6, 300)
   * undefined
   * @param {number} retries - Number of retry attempts to wait for the Stripe element containers to become available.
   * @param {number} delay - Delay in milliseconds between retry attempts.
   * @returns {void} No value is returned.
   */
  private initializeAndMountStripeElements(retries = 6, delay = 300): void {
    setTimeout(() => {
      if (this.state.paymentForm.method !== PaymentMethodCode.Card) return;
      const cardNumberContainer = document.getElementById('card-number');
      const cardExpiryContainer = document.getElementById('card-expiry');
      const cardCvcContainer = document.getElementById('card-cvc');
      if (!cardNumberContainer || !cardExpiryContainer || !cardCvcContainer) {
        if (retries > 0) {
          this.initializeAndMountStripeElements(retries - 1, delay);
        }
        return;
      }
      if (!this.stripe) {
        this.initializeStripeIfNeeded().then(() => this.mountStripeElements());
      } else if (this.cardNumberElement) {
        this.mountStripeElements();
      } else {
        this.initializeStripeIfNeeded().then(() => this.mountStripeElements());
      }
    }, delay);
  }

  /**
   * Initializes Stripe and creates payment elements if they have not been created yet.
   * @example
   * initializeStripeIfNeeded()
   * Promise<void>
   * @returns {Promise<void>} Resolves when Stripe is initialized or if it is already available.
   **/
  private async initializeStripeIfNeeded(): Promise<void> {
    if (this.stripe && this.cardNumberElement) return;
    try {
      this.stripe = await loadStripe(environment.stripePublicKey);
      if (!this.stripe) throw new Error('Failed to load Stripe');
      this.elements = this.stripe.elements({ appearance: STRIPE_ELEMENTS_APPEARANCE });
      this.cardNumberElement = this.elements.create(
        'cardNumber',
        createStripeElementConfig('8539 5932 9402 4739')
      );
      this.cardExpiryElement = this.elements.create(
        'cardExpiry',
        createStripeElementConfig('05/30')
      );
      this.cardCvcElement = this.elements.create('cardCvc', createStripeElementConfig('123'));
    } catch (error) {
      console.error('Error initializing Stripe:', error);
    }
  }

  /**
   * Sets up Stripe element event listeners to display validation errors and update completion state.
   * @example
   * setupStripeElementListeners(element, errorElementId)
   * undefined
   * @param {any} element - The Stripe element instance to attach listeners to.
   * @param {string} errorElementId - The DOM id of the element used to display validation errors.
   * @returns {void} Does not return a value.
   */
  private setupStripeElementListeners(element: any, errorElementId: string): void {
    const errorElement = document.getElementById(errorElementId);
    if (!errorElement) return;
    element.on('change', (event: any) => {
      if (event.error) {
        errorElement.textContent = event.error.message;
        errorElement.style.display = 'block';
        this.updateStripeElementCompletionState(errorElementId, false);
      } else {
        errorElement.textContent = '';
        errorElement.style.display = 'none';
        this.updateStripeElementCompletionState(errorElementId, event.complete || false);
      }
    });
    element.on('focus', () => {
      errorElement.textContent = '';
      errorElement.style.display = 'none';
    });
  }

  private updateStripeElementCompletionState(errorElementId: string, isComplete: boolean): void {
    if (errorElementId === 'card-number-error') this.state.stripeCardNumberComplete = isComplete;
    else if (errorElementId === 'card-expiry-error')
      this.state.stripeCardExpiryComplete = isComplete;
    else if (errorElementId === 'card-cvc-error') this.state.stripeCardCvcComplete = isComplete;
  }

  private unmountStripeElements(): void {
    [this.cardNumberElement, this.cardExpiryElement, this.cardCvcElement].forEach(el => {
      if (el)
        try {
          el.unmount();
        } catch {
          /* ignore */
        }
    });
  }

  private mapOrderStatus(orderStatus: string, paymentStatus: string): any {
    return OrderUtils.mapOrderStatus(orderStatus, paymentStatus);
  }

  /**
   * Generates and downloads an invoice using default customer, business, and payment details.
   * @example
   * generateInvoiceWithDefaults(transaction)
   * void
   * @param {PaymentTransactionHistory} transaction - Payment transaction data used to build the invoice.
   * @returns {void} Does not return a value.
   **/
  private downloadInvoiceForOrder(
    transaction: PaymentTransactionHistory,
    order: Order,
    cardDetails: CardDetails | null,
    billingDetails: BillingDetails | null
  ): void {
    this.profileService.getBusinessProfile().subscribe({
      next: response => {
        if (response.status && response.data) {
          const businessData = response.data;
          const invoiceData: InvoiceData = {
            order,
            transaction,
            customerInfo: {
              name: order.customerName || 'N/A',
              email: order.email || 'N/A',
              phone: order.phone || 'N/A',
              address: order.branch || 'N/A',
            },
            businessInfo: {
              name: businessData.name || 'Way.com Car Wash',
              address: '',
              phone: businessData.contact || '(555) 123-4567',
              email: businessData.email || 'support@way.com',
            },
            cardDetails,
            billingDetails,
          };
          try {
            this.pdfService.generateInvoice(invoiceData);
            setTimeout(() => {
              this.messageService.add({
                severity: 'success',
                summary: 'Invoice saved to device!',
                detail: 'The invoice has been downloaded to your device.',
              });
            }, 100);
          } catch {
            this.messageService.add({
              severity: 'error',
              summary: 'Download failed',
              detail: 'There was an error generating the invoice. Please try again.',
            });
          }
        } else {
          this.generateInvoiceWithDefaults(transaction, order, cardDetails, billingDetails);
        }
      },
      error: () =>
        this.generateInvoiceWithDefaults(transaction, order, cardDetails, billingDetails),
    });
  }

  private generateInvoiceWithDefaults(
    transaction: PaymentTransactionHistory,
    order: Order,
    cardDetails: CardDetails | null,
    billingDetails: BillingDetails | null
  ): void {
    const invoiceData: InvoiceData = {
      order,
      transaction,
      customerInfo: {
        name: order.customerName || 'N/A',
        email: order.email || 'N/A',
        phone: order.phone || 'N/A',
        address: order.branch || 'N/A',
      },
      businessInfo: {
        name: 'Way.com Car Wash',
        address: '',
        phone: '(555) 123-4567',
        email: 'support@way.com',
      },
      cardDetails,
      billingDetails,
    };
    try {
      this.pdfService.generateInvoice(invoiceData);
      setTimeout(() => {
        this.messageService.add({
          severity: 'success',
          summary: 'Invoice saved to device!',
          detail: 'The invoice has been downloaded to your device.',
        });
      }, 100);
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: 'Download failed',
        detail: 'There was an error generating the invoice. Please try again.',
      });
    }
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
